import React from 'react';

/**
 * The one catalogue of Add/Edit forms in the app.
 *
 * Every entry is consumed by THREE surfaces, which is the whole reason this
 * file exists: the floating form window (`<FormWindowProvider>`), the
 * deep-linkable full page (`<FormFullPage>`), and the router + AppShell
 * allow-list (`routes/shellRoutes.js`). Registering a form here is what makes
 * `/leads/new` and the "Add Lead" modal render the SAME component — a second
 * hand-maintained list is how those two silently drift into different forms.
 *
 * ── The form-component contract ──────────────────────────────────────────
 * Every `Component` below receives exactly these props:
 *
 *   surface       'modal' | 'page'  — for layout only, never for behaviour
 *   recordId      number | null     — edit forms only, resolved from the URL
 *                                     param on the page surface
 *   record        object | null     — the row a list page already had in hand,
 *                                     so an edit modal opened from a row does
 *                                     not re-fetch what the table just showed
 *   initialValues object | null     — a draft carried across from the modal
 *                                     when the user hits "View full page"
 *   apiRef        ref               — the form assigns
 *                                     `{ getValues, isDirty }` so the shell can
 *                                     carry the draft across surfaces and warn
 *                                     before discarding typed work
 *   onDone        () => void        — call after a successful submit
 *   onCancel      () => void        — call to back out
 *
 * `fullPagePath` is OPTIONAL and its absence is meaningful: a form with no
 * page (the two-field lead assign) hides the "View full page" control rather
 * than offering a button that goes nowhere.
 */

const LeadForm = React.lazy(() => import('./LeadForm'));
const LeadAssignForm = React.lazy(() => import('./LeadAssignForm'));
const OrganizationForm = React.lazy(() => import('./OrganizationForm'));
const ContactForm = React.lazy(() => import('./ContactForm'));
const TagForm = React.lazy(() => import('./TagForm'));
const OpportunityForm = React.lazy(() => import('./OpportunityForm'));
const ActivityForm = React.lazy(() => import('./ActivityForm'));
const SalesTeamForm = React.lazy(() => import('./SalesTeamForm'));
const AssignmentRuleForm = React.lazy(() => import('./AssignmentRuleForm'));
const PipelineStageForm = React.lazy(() => import('./PipelineStageForm'));
const OauthUserForm = React.lazy(() => import('./OauthUserForm'));
const OauthUserAccessForm = React.lazy(() => import('./OauthUserAccessForm'));
const OauthClientForm = React.lazy(() => import('./OauthClientForm'));
const ShareTokenForm = React.lazy(() => import('./ShareTokenForm'));

export const FORM_REGISTRY = {
  // ── CRM core ───────────────────────────────────────────────────────────
  'lead.create': {
    Component: LeadForm,
    titleKey: 'forms.leadCreate',
    fullPagePath: '/leads/new',
    returnPath: '/leads',
    requiredScope: 'leads.create',
    width: 'md',
  },
  'lead.assign': {
    // No page: a two-field owner picker does not deserve a URL, and offering
    // "View full page" on it would be a control that leads somewhere emptier.
    Component: LeadAssignForm,
    titleKey: 'forms.leadAssign',
    requiredScope: 'leads.assign',
    width: 'sm',
  },
  'organization.create': {
    Component: OrganizationForm,
    titleKey: 'forms.organizationCreate',
    fullPagePath: '/organizations/new',
    returnPath: '/organizations',
    requiredScope: 'organizations.create',
    width: 'md',
  },
  'contact.create': {
    Component: ContactForm,
    titleKey: 'forms.contactCreate',
    fullPagePath: '/contacts/new',
    returnPath: '/contacts',
    requiredScope: 'contacts.create',
    width: 'md',
  },
  'tag.create': {
    Component: TagForm,
    titleKey: 'forms.tagCreate',
    fullPagePath: '/tags/new',
    returnPath: '/tags',
    requiredScope: 'tags.create',
    width: 'sm',
  },
  'tag.edit': {
    // No page: the API has no GET /v1/tags/{id}, so a bookmarked
    // `/tags/7/edit` could not load the row it claims to edit. A control that
    // only works when you arrive through the modal is worse than no control.
    Component: TagForm,
    titleKey: 'forms.tagEdit',
    returnPath: '/tags',
    requiredScope: 'tags.update',
    width: 'sm',
  },
  'opportunity.create': {
    Component: OpportunityForm,
    titleKey: 'forms.opportunityCreate',
    fullPagePath: '/opportunities/new',
    returnPath: '/opportunities',
    requiredScope: 'opportunities.create',
    width: 'md',
  },
  'activity.compose': {
    Component: ActivityForm,
    titleKey: 'forms.activityCompose',
    fullPagePath: '/activities/new',
    returnPath: '/activities',
    requiredScope: 'activities.create',
    width: 'md',
  },

  // ── Sales administration ───────────────────────────────────────────────
  'salesTeam.create': {
    Component: SalesTeamForm,
    titleKey: 'forms.salesTeamCreate',
    fullPagePath: '/sales-teams/new',
    returnPath: '/sales-teams',
    requiredScope: 'sales-teams.create',
    width: 'sm',
  },
  'assignmentRule.create': {
    Component: AssignmentRuleForm,
    titleKey: 'forms.assignmentRuleCreate',
    fullPagePath: '/assignment-rules/new',
    returnPath: '/assignment-rules',
    requiredScope: 'sales-teams.manage',
    width: 'md',
  },
  'pipelineStage.create': {
    Component: PipelineStageForm,
    titleKey: 'forms.pipelineStageCreate',
    fullPagePath: '/pipeline-settings/stages/new',
    returnPath: '/pipeline-settings',
    requiredScope: 'pipelines.update',
    width: 'md',
  },
  'pipelineStage.edit': {
    Component: PipelineStageForm,
    titleKey: 'forms.pipelineStageEdit',
    fullPagePath: '/pipeline-settings/stages/:stageId/edit',
    paramKey: 'stageId',
    returnPath: '/pipeline-settings',
    requiredScope: 'pipelines.update',
    width: 'md',
  },

  // ── Platform administration ────────────────────────────────────────────
  'oauthUser.create': {
    Component: OauthUserForm,
    titleKey: 'forms.oauthUserCreate',
    fullPagePath: '/admin/users/new',
    returnPath: '/admin/users',
    requiredScope: 'oauth-users.create',
    width: 'md',
  },
  'oauthUser.access': {
    // No page: `/admin/users` has no single-user read hook to cold-load a deep
    // link with, and a privilege editor that renders empty on refresh is worse
    // than one that only opens from the row you picked. Rule 78 — this is one
    // of only two writers of role/scopes, and it stays admin-gated.
    Component: OauthUserAccessForm,
    titleKey: 'forms.oauthUserAccess',
    returnPath: '/admin/users',
    requiredScope: 'oauth-users.permissions.update',
    width: 'lg',
  },
  'oauthClient.create': {
    Component: OauthClientForm,
    titleKey: 'forms.oauthClientCreate',
    fullPagePath: '/admin/oauth-clients/new',
    returnPath: '/admin/oauth-clients',
    requiredScope: 'oauth-clients.create',
    width: 'md',
  },
  'shareToken.edit': {
    // No page, same reason as `tag.edit` — the share-token API exposes a list
    // and an update, but no single-record read to cold-load a deep link with.
    Component: ShareTokenForm,
    titleKey: 'forms.shareTokenEdit',
    returnPath: '/admin/upload-s3-public-share-tokens',
    requiredScope: 'upload-s3-public-share-token.update',
    width: 'md',
  },
};

/**
 * The full-page routes, derived from the registry rather than hand-listed —
 * `routes/shellRoutes.js` feeds the paths to BOTH the router and AppShell's
 * "is this a real destination?" gate, and `routes/index.jsx` renders the
 * element, so a form can never be page-addressable in one and unknown in the
 * other.
 */
export const FORM_FULL_PAGE_ROUTES = Object.entries(FORM_REGISTRY)
  .filter(([, def]) => Boolean(def.fullPagePath))
  .map(([formKey, def]) => ({
    formKey,
    path: def.fullPagePath,
    paramKey: def.paramKey ?? null,
  }));

export default FORM_REGISTRY;
